import { GeoObject } from '@data/geodata/legacy/GeoObject';
import { ControllerManager } from '@gui/controller/ControllerManager';
import { ColorModeOptions } from '@gui/constants/ColorModeOptions';
import { Panels } from '@gui/constants/Panels';
import usePersistentStore, { getGlobalState, getNewColorScheme, useGlobalBucket } from '@gui/store/persistent/persistentStore';
import { ThemeMode } from '@gui/theme';
import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Divider,
    FormControl,
    FormControlLabel,
    FormGroup,
    InputLabel,
    MenuItem,
    Popover,
    Radio,
    RadioGroup,
    Select,
    Stack,
    Typography,
} from '@mui/material';
import type { AmbientState, GeoObjectMode, SettingsState } from '@visualizer/types';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { getGrid } from 'store/LoadingStore';
import { getViewer } from 'store/viewerStore';
import { refreshContext } from '../../../../Graphics/standardRenderer';
import { deleteGrid, getStorageEstimateInfo, listCachedGrids } from '../../../../Loader/IndexedDB_Handler';
import { Visualization } from '../../../../rendering/Visualizer';
import { ApiRoutes } from '../../../../store/ApiRoutes';
import CustomTooltip from '@gui/components/generic/CustomTooltip';
import CoordinateSystemSelectorControl from '@gui/components/generic/CoordinateSystemSelectorControl';
import { COLUMN_WIDTH } from './SidePaneDialog';
import useNotificationStore from '@gui/store/notification';
import { NotificationType } from '@visualizer/types';

const THEME_OPTIONS: { value: ThemeMode; label: string }[] = [
    { value: 'light', label: 'Light' },
    { value: 'dark', label: 'Dark' },
];

interface SettingsMenuProps {
    viewer: Visualization;
    showContent: boolean;
}

type StorageEstimateInfo = {
    usageBytes: number;
    quotaBytes: number;
    freeBytes: number;
    safeFreeBytes: number;
    indexedDbUsageBytes?: number | null;
};

type CachedGridInfo = {
    name: string;
    gridName: string | null;
    lastAccess: number | null;
    isCurrentGrid: boolean;
};

const DISPLAY_MODE_OPTIONS: { value: GeoObjectMode; label: string }[] = [
    { value: 'standard', label: 'Standard' },
    { value: 'render', label: 'Rendered' },
    { value: 'hybrid', label: 'Hybrid (Std buildings + Rendered terrain)' },
];

const SCENE_COLOR_MODE_OPTIONS: ColorModeOptions[] = [
    ColorModeOptions.Colored,
    ColorModeOptions.Dark,
    ColorModeOptions.LightGrey,
    ColorModeOptions.LightGreyBlueGreen2,
    ColorModeOptions.WhiteMode,
];

const toSceneColorModeSelectValue = (colorMode: string): ColorModeOptions => {
    if (colorMode === ColorModeOptions.LightGreyBlueGreen) return ColorModeOptions.LightGrey;
    if (colorMode === ColorModeOptions.WhiteBlueGreen) return ColorModeOptions.WhiteMode;

    if ((SCENE_COLOR_MODE_OPTIONS as string[]).includes(colorMode)) return colorMode as ColorModeOptions;

    // Safe fallback:
    return ColorModeOptions.LightGrey;
};

export const useAutoSaveState = (options?: { enabled?: boolean }): void => {
    const canUseApi = ApiRoutes.canUseApi();
    const enabled = options?.enabled ?? true;

    useEffect(() => {
        if (!canUseApi || !enabled) return;
        const stateController = ControllerManager.getInstance().getStateController();

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'hidden') {
                void stateController.uploadAutomaticStateNow();
            }
        };

        const handlePageHide = () => {
            void stateController.uploadAutomaticStateNow();
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('pagehide', handlePageHide);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('pagehide', handlePageHide);
        };
    }, [canUseApi, enabled]);
};

const SettingsMenu: React.FC<SettingsMenuProps> = ({ viewer, showContent }) => {
    const setPanel = usePersistentStore((state) => state.setPanel);
    const setSettingsPanel = (updates: Partial<SettingsState>) => setPanel(updates, Panels.SETTINGS);
    const setAmbientPanel = (updates: Partial<AmbientState>) => setPanel(updates, Panels.AMBIENT);
    const showNotification = useNotificationStore((s) => s.showNotification);

    const { colorScheme: colorMode, coloredItems, lowPerformance, themeMode } = useGlobalBucket((state) => state.settings);
    const ambient = useGlobalBucket((s) => s.ambient);
    const isLightGreyToggleFamily = colorMode === ColorModeOptions.LightGrey || colorMode === ColorModeOptions.LightGreyBlueGreen;
    const isWhiteToggleFamily = colorMode === ColorModeOptions.WhiteMode || colorMode === ColorModeOptions.WhiteBlueGreen;
    const showColoredItemsToggle = isLightGreyToggleFamily || isWhiteToggleFamily;
    const coloredItemsChecked = coloredItems;
    const sceneColorModeSelectValue = toSceneColorModeSelectValue(colorMode);

    const fpsIndicatorIsVisible = useGlobalBucket((s) => s.overlays.fpsIndicatorVisible);
    const isDevelopment = import.meta.env.MODE === 'development';
    const panelRef = useRef<HTMLDivElement | null>(null);
    const [storageInfo, setStorageInfo] = useState<StorageEstimateInfo | null>(null);
    const [cachedGrids, setCachedGrids] = useState<CachedGridInfo[]>([]);
    const [storageLoading, setStorageLoading] = useState(false);
    const [deletePopoverOpen, setDeletePopoverOpen] = useState(false);
    const [selectedGrids, setSelectedGrids] = useState<string[]>([]);
    const [isDeletingGrids, setIsDeletingGrids] = useState(false);

    const formatMegabytes = useCallback((value: number) => {
        if (!Number.isFinite(value) || value <= 0) return '0 MB';
        return `${(value / 1024 ** 2).toFixed(0)} MB`;
    }, []);

    const formatLastAccess = useCallback((value: number | null) => {
        if (value === null || !Number.isFinite(value)) return 'N/A';
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return 'N/A';
        return date.toLocaleString();
    }, []);

    const loadStorageData = useCallback(async () => {
        setStorageLoading(true);
        try {
            const [estimate, grids] = await Promise.all([getStorageEstimateInfo(), listCachedGrids()]);
            const deletableGrids = grids.filter((grid) => !grid.isCurrentGrid);
            setStorageInfo(estimate);
            setCachedGrids(deletableGrids);
            setSelectedGrids((previousSelected) =>
                previousSelected.filter((gridName) => deletableGrids.some((grid) => grid.name === gridName))
            );
        } catch (error) {
            console.error(error);
            showNotification('Unable to read cached storage information.', NotificationType.Error);
        } finally {
            setStorageLoading(false);
        }
    }, [showNotification]);

    const toggleGridSelection = useCallback((gridName: string, checked: boolean) => {
        setSelectedGrids((previousSelected) => {
            if (checked) return Array.from(new Set([...previousSelected, gridName]));
            return previousSelected.filter((selectedGrid) => selectedGrid !== gridName);
        });
    }, []);

    const handleDeleteSelectedGrids = useCallback(async () => {
        if (!selectedGrids.length) return;
        setIsDeletingGrids(true);
        try {
            const results = await Promise.allSettled(selectedGrids.map((gridName) => deleteGrid(gridName)));
            const failedCount = results.filter((result) => result.status === 'rejected').length;
            const deletedCount = results.length - failedCount;

            if (deletedCount > 0 && failedCount === 0) {
                showNotification('Selected cache deleted.', NotificationType.Success);
            } else if (deletedCount > 0) {
                showNotification(`Deleted ${deletedCount} cache item(s). ${failedCount} failed.`, NotificationType.Warning);
            } else {
                showNotification('Unable to delete selected cache.', NotificationType.Error);
            }
            setDeletePopoverOpen(false);
            setSelectedGrids([]);
            await loadStorageData();
        } catch (error) {
            console.error(error);
            showNotification('Unable to delete selected cache.', NotificationType.Error);
        } finally {
            setIsDeletingGrids(false);
        }
    }, [loadStorageData, selectedGrids, showNotification]);

    useEffect(() => {
        getGrid()
            .getActiveGeodata()
            .getIteratorLegacy()
            .forEach((geoObject: GeoObject) => {
                geoObject.updateColors();
            });
        getGrid()
            .getActiveGeodata()
            .getIterator()
            .forEach((geoObject) => {
                geoObject.changeColorScheme(getNewColorScheme());
            });
        getViewer().updateBackground();
    }, [colorMode]);

    useEffect(() => {
        refreshContext(getNewColorScheme(), ambient.mode, getGlobalState().context);
        viewer.postProcess.applyPostProcess();
    }, [lowPerformance, viewer]);

    useEffect(() => {
        if (!showContent) return;
        void loadStorageData();
    }, [loadStorageData, showContent]);

    const handleDisplayModeChange = (mode: GeoObjectMode) => {
        setAmbientPanel({ mode });
    };

    if (!showContent) return null;

    return (
        <>
            <Box ref={panelRef}>
                <Stack>
                    <Typography sx={{ mb: 1.5 }} fontWeight="bold">
                        Colors
                    </Typography>
                    <FormGroup>
                        <FormControl fullWidth sx={{ mb: 1.5 }}>
                            <InputLabel id="color-mode-label">Scene Color Mode</InputLabel>
                            <Select
                                labelId="color-mode-label"
                                value={sceneColorModeSelectValue}
                                size="small"
                                label="Scene Color Mode"
                                onChange={(event) => {
                                    const selected = event.target.value as ColorModeOptions;

                                    if (selected === ColorModeOptions.LightGrey) {
                                        setSettingsPanel({
                                            colorScheme: coloredItems ? ColorModeOptions.LightGreyBlueGreen : ColorModeOptions.LightGrey,
                                        });
                                        return;
                                    }
                                    if (selected === ColorModeOptions.WhiteMode) {
                                        setSettingsPanel({
                                            colorScheme: coloredItems ? ColorModeOptions.WhiteBlueGreen : ColorModeOptions.WhiteMode,
                                        });
                                        return;
                                    }

                                    setSettingsPanel({ colorScheme: selected });
                                }}
                            >
                                {SCENE_COLOR_MODE_OPTIONS.map((option) => (
                                    <MenuItem key={option} value={option}>
                                        {option}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl fullWidth>
                            <InputLabel id="theme-mode-label">Gui Color Mode</InputLabel>
                            <Select
                                labelId="theme-mode-label"
                                value={themeMode}
                                size="small"
                                label="Gui Color Mode"
                                onChange={(event) => setSettingsPanel({ themeMode: event.target.value as ThemeMode })}
                            >
                                {THEME_OPTIONS.map(({ value, label }) => (
                                    <MenuItem key={value} value={value}>
                                        {label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </FormGroup>

                    <Divider sx={{ mt: 1.5 }}></Divider>

                    <FormControl component="fieldset" sx={{ mt: 1 }}>
                        <Typography fontWeight={'bold'}>Display mode</Typography>
                        <RadioGroup
                            className="panel-compact-control-stack"
                            value={ambient.mode}
                            onChange={(e) => handleDisplayModeChange(e.target.value as GeoObjectMode)}
                            sx={{ gap: 0.5, '& .MuiFormControlLabel-root': { m: 0 } }}
                        >
                            {DISPLAY_MODE_OPTIONS.map((option) => (
                                <FormControlLabel
                                    key={option.value}
                                    value={option.value}
                                    control={<Radio size="small" />}
                                    label={option.label}
                                />
                            ))}
                        </RadioGroup>
                        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={ambient.showStrokes}
                                        onChange={(event) => setAmbientPanel({ showStrokes: event.target.checked })}
                                        name="showStrokes"
                                    />
                                }
                                label="Strokes"
                            />
                            {showColoredItemsToggle && (
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={coloredItemsChecked}
                                            onChange={(event) => {
                                                const nextChecked = event.target.checked;
                                                if (isLightGreyToggleFamily) {
                                                    setSettingsPanel({
                                                        coloredItems: nextChecked,
                                                        colorScheme: nextChecked
                                                            ? ColorModeOptions.LightGreyBlueGreen
                                                            : ColorModeOptions.LightGrey,
                                                    });
                                                    return;
                                                }
                                                if (isWhiteToggleFamily) {
                                                    setSettingsPanel({
                                                        coloredItems: nextChecked,
                                                        colorScheme: nextChecked
                                                            ? ColorModeOptions.WhiteBlueGreen
                                                            : ColorModeOptions.WhiteMode,
                                                    });
                                                }
                                            }}
                                            name="coloredItems"
                                        />
                                    }
                                    label="Colored items"
                                />
                            )}
                        </Box>
                    </FormControl>

                    <Divider sx={{ mt: 1 }}></Divider>

                    <Box sx={{ mt: 1, mb: 1 }} className="panel-compact-control-stack">
                        <CoordinateSystemSelectorControl title="Coordinates System" />
                    </Box>

                    <Divider sx={{ mb: 1 }}></Divider>

                    <Box sx={{ mt: 1, mb: 1 }} className="panel-compact-control-stack">
                        <Typography fontWeight={'bold'}>Performance</Typography>
                        {isDevelopment && (
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={fpsIndicatorIsVisible}
                                            onChange={(event) => setPanel({ fpsIndicatorVisible: event.target.checked }, Panels.OVERLAYS)}
                                            name="showFpsIndicator"
                                        />
                                    }
                                    label={'Show FPS Indicator'}
                                />
                            </Box>
                        )}
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={lowPerformance}
                                    onChange={(event) => setSettingsPanel({ lowPerformance: event.target.checked })}
                                    name="lowPerformanceMode"
                                />
                            }
                            label="Low Quality Mode"
                        />
                    </Box>

                    <Divider sx={{ mb: 1 }}></Divider>

                    <Box sx={{ mt: 1, mb: 1 }} className="panel-compact-control-stack">
                        <Typography fontWeight={'bold'}>Storage</Typography>
                        {storageLoading ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CircularProgress size={14} />
                                <Typography variant="body2">Loading storage info...</Typography>
                            </Box>
                        ) : storageInfo ? (
                            <Stack spacing={0.25}>
                                <Typography variant="body2">
                                    Cache memory used: {formatMegabytes(storageInfo.indexedDbUsageBytes ?? storageInfo.usageBytes)}
                                </Typography>
                            </Stack>
                        ) : (
                            <Typography variant="body2">Storage information is not available in this browser.</Typography>
                        )}
                        <Button
                            variant="contained"
                            size="small"
                            sx={{ mt: 1, width: 'fit-content' }}
                            onClick={() => {
                                setDeletePopoverOpen(true);
                                void loadStorageData();
                            }}
                            disabled={storageLoading}
                        >
                            Delete cache
                        </Button>
                    </Box>
                </Stack>
            </Box>

            <Popover
                open={deletePopoverOpen}
                onClose={() => setDeletePopoverOpen(false)}
                anchorEl={panelRef.current}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                PaperProps={{
                    sx: {
                        p: 2,
                        width: `${COLUMN_WIDTH - 32}px`,
                        maxWidth: `${COLUMN_WIDTH - 32}px`,
                        boxShadow: 4,
                    },
                }}
            >
                <Stack spacing={2}>
                    <Typography variant="body2">Please check State section to manage current grid cache.</Typography>
                    {cachedGrids.length ? (
                        <>
                            <Typography variant="body2">Select one or more other cached grids to delete.</Typography>
                            <FormGroup>
                                {cachedGrids.map((grid) => (
                                    <FormControlLabel
                                        key={grid.name}
                                        control={
                                            <Checkbox
                                                checked={selectedGrids.includes(grid.name)}
                                                onChange={(event) => toggleGridSelection(grid.name, event.target.checked)}
                                            />
                                        }
                                        label={
                                            <Box sx={{ display: 'inline-flex', alignItems: 'center', lineHeight: 1.2 }}>
                                                <CustomTooltip
                                                    side="right"
                                                    mode="hover"
                                                    title={
                                                        <Box sx={{ lineHeight: 1.4 }}>
                                                            <span>Last access: {formatLastAccess(grid.lastAccess)}</span>
                                                            <br />
                                                            <span>Grid ID: {grid.name}</span>
                                                        </Box>
                                                    }
                                                >
                                                    <span>{grid.gridName ?? grid.name}</span>
                                                </CustomTooltip>
                                            </Box>
                                        }
                                    />
                                ))}
                            </FormGroup>
                        </>
                    ) : (
                        <Typography variant="body2">No other cached grids to delete.</Typography>
                    )}
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button
                            variant="text"
                            onClick={() => {
                                setDeletePopoverOpen(false);
                                setSelectedGrids([]);
                            }}
                            disabled={isDeletingGrids}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={() => void handleDeleteSelectedGrids()}
                            disabled={!selectedGrids.length || isDeletingGrids}
                        >
                            {isDeletingGrids ? <CircularProgress size={14} color="inherit" /> : 'Delete'}
                        </Button>
                    </Stack>
                </Stack>
            </Popover>
        </>
    );
};

export default SettingsMenu;
